import { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import Layout from "@/components/feature/Layout";
import JsonLd from "@/components/feature/JsonLd";
import Breadcrumb from "@/components/feature/Breadcrumb";
import ArchiveCard from "@/components/feature/ArchiveCard";
import usePageMeta from "@/hooks/usePageMeta";
import { archives, categories, siteInfo } from "@/mocks/archive";

export default function ArchiveList() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [query, setQuery] = useState(searchParams.get("q") ?? "");
  const [activeCat, setActiveCat] = useState<string>("all");

  usePageMeta({
    title: "考古档案总览｜黑料网红黑历史 · 旧言论与前科记录列表",
    description:
      "溯影档案的考古档案总览，按早期言论、旧照对比、前科记录、人设落差、凭证核对、搬运考据六类归档，支持关键词搜索，快速定位你想考古的那一段黑料网红黑历史。",
    keywords: "黑料网红黑历史,黑料网红考古,黑料网红旧言论,档案列表,黑料网红前科",
    canonical: "https://scadaforweb.cn/archive",
  });

  const filtered = useMemo(() => {
    const kw = query.trim().toLowerCase();
    return archives.filter((item) => {
      const catOk = activeCat === "all" || item.categorySlug === activeCat;
      if (!catOk) return false;
      if (!kw) return true;
      const haystack = [item.title, item.summary, item.category, ...item.tags]
        .join(" ")
        .toLowerCase();
      return haystack.includes(kw);
    });
  }, [query, activeCat]);

  const handleQueryChange = (value: string) => {
    setQuery(value);
    const next = new URLSearchParams(searchParams);
    if (value.trim()) next.set("q", value);
    else next.delete("q");
    setSearchParams(next, { replace: true });
  };

  const catOptions = [{ slug: "all", name: "全部档案", count: archives.length }, ...categories];

  return (
    <Layout>
      <JsonLd
        id="ld-collection-archive"
        data={{
          "@context": "https://schema.org",
          "@type": "CollectionPage",
          name: "考古档案总览",
          description: "按六类主题归档的黑料网红公开言行回溯档案列表。",
          url: "https://scadaforweb.cn/archive",
          inLanguage: "zh-CN",
          isPartOf: { "@type": "WebSite", name: siteInfo.brand, url: "https://scadaforweb.cn/" },
          breadcrumb: {
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "首页", item: "https://scadaforweb.cn/" },
              { "@type": "ListItem", position: 2, name: "考古档案", item: "https://scadaforweb.cn/archive" },
            ],
          },
          mainEntity: {
            "@type": "ItemList",
            numberOfItems: filtered.length,
            itemListElement: filtered.map((item, index) => ({
              "@type": "ListItem",
              position: index + 1,
              name: item.title,
              url: `https://scadaforweb.cn/archive/${item.slug}`,
            })),
          },
        }}
      />

      <section className="w-full px-4 md:px-8 pt-8 md:pt-10 pb-8 md:pb-10 bg-background-100 border-b border-background-300">
        <Breadcrumb items={[{ name: "首页", to: "/" }, { name: "考古档案" }]} />
        <h1 className="mt-4 font-heading text-2xl md:text-4xl font-bold text-foreground-950 leading-snug">
          考古档案总览
        </h1>
        <p className="mt-3 max-w-3xl text-sm md:text-base text-foreground-600 leading-relaxed">
          这里收录 {siteInfo.brand} 已公开的 {archives.length} 篇示例档案。按早期言论、旧照对比、前科记录、
          人设落差、凭证核对与搬运考据六类归置，可用关键词搜索。每篇都标注了出处与存疑点。
        </p>
      </section>

      <section className="w-full px-4 md:px-8 py-8 md:py-10">
        <div className="flex flex-col lg:flex-row lg:items-center gap-4 mb-6">
          <label className="relative flex-1">
            <span className="sr-only">搜索档案关键词</span>
            <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-base"></i>
            <input
              type="search"
              value={query}
              onChange={(e) => handleQueryChange(e.target.value)}
              placeholder="搜索关键词，例如：口头禅、旧照、捐款、小号"
              className="w-full pl-10 pr-4 py-2.5 text-sm rounded-md bg-background-100 border border-background-300 text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-400"
            />
          </label>
          <p className="text-sm text-foreground-600 whitespace-nowrap">
            共找到 <strong className="text-primary-600">{filtered.length}</strong> 篇档案
          </p>
        </div>

        <div className="inline-flex flex-wrap gap-1.5 p-1 rounded-full bg-background-100 border border-background-300 mb-8">
          {catOptions.map((cat) => (
            <button
              key={cat.slug}
              type="button"
              onClick={() => setActiveCat(cat.slug)}
              className={`px-4 py-2 text-xs md:text-sm rounded-full transition-colors whitespace-nowrap ${
                activeCat === cat.slug
                  ? "bg-primary-500 text-background-50"
                  : "text-foreground-700 hover:bg-background-200"
              }`}
            >
              {cat.name}
            </button>
          ))}
        </div>

        {filtered.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
            {filtered.map((item) => (
              <ArchiveCard key={item.slug} archive={item} />
            ))}
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center py-20 text-center">
            <span className="w-14 h-14 flex items-center justify-center rounded-full bg-background-100 text-foreground-400 text-2xl mb-4">
              <i className="ri-folder-open-line"></i>
            </span>
            <p className="font-heading text-lg text-foreground-950 mb-2">没有匹配的档案</p>
            <p className="text-sm text-foreground-600 mb-5">
              换个关键词试试，或者清空筛选看看全部 {archives.length} 篇。
            </p>
            <button
              type="button"
              onClick={() => {
                setActiveCat("all");
                handleQueryChange("");
              }}
              className="px-5 py-2.5 rounded-md bg-foreground-950 text-background-50 text-sm hover:bg-foreground-800 transition-colors whitespace-nowrap"
            >
              清空全部筛选
            </button>
          </div>
        )}
      </section>
    </Layout>
  );
}